import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {FormattedMessage} from "react-intl";

// Components
import BottomDrawer from "components/WMS/Mobile/BottomDrawer";
import ToggleNavigation from "components/WMS/Mobile/Toggle/ToggleNavigation";
import Toggle from "components/WMS/Mobile/Toggle/Toggle";
import RetailerNameDrawerTitle from "components/WMS/Mobile/WmsItemList/components/RetailerNameDrawerTitle";
import PickingActions from "components/WMS/Mobile/WmsItemList/components/PickingActions";
import WmsItemInformationDrawer from "components/WMS/Mobile/WmsItemList/components/WmsItemInformationDrawer";
import RetailerItemsList from "./components/RetailerItemsList";
import RemoveFromTrolleyAction from "components/WMS/Mobile/WmsItemList/components/RemoveFromTrolleyAction";

// Contexts
import {MissionContext} from "../../context/MissionContext";

// Utils
import { WmsItemsStatus } from "utils/WmsItemsStatus";

// Assets
import {greyishBlue50} from "assets/jss/main";

RetailerItemsDrawer.propTypes = {
    open: PropTypes.bool.isRequired,
    handleClose: PropTypes.func.isRequired,
    wmsItems: PropTypes.arrayOf(
        PropTypes.shape({
            status: PropTypes.string.isRequired,
            lineItem: PropTypes.shape({
                retailer: PropTypes.object
            })
        })
    ).isRequired,
    selectedFilter: PropTypes.number.isRequired
};

function RetailerItemsDrawer({open, handleClose, wmsItems, selectedFilter, selectedRetailer}) {
    const [retailerItems, setRetailerItems] = useState([]);
    const [filteredItems, setFilteredItems] = useState([]);
    const [cancelledItems, setCancelledItems] = useState([]);

    const [itemsToPick, setItemsToPick] = useState(0)
    const [itemsInTrolley, setItemsInTrolley] = useState(0)
    const [itemsCancelled, setItemsCancelled] = useState(0);

    const [filterValue, setFilterValue] = useState(selectedFilter);

    const [selectedWmsItem, setSelectedWmsItem] = useState(null);

    const { getPickingTotal, getInTrolleyTotal, getCancelledTotal, refreshMission } = React.useContext(MissionContext);

    // when selected retailer change or if wmsItems list change (because of refresh)
    useEffect(() => {
        if (!selectedRetailer) {
            return;
        }

        const items = wmsItems.filter(item => item.lineItem.retailer.id === selectedRetailer.id);

        setRetailerItems(items);

        setItemsToPick(getPickingTotal(items));
        setItemsInTrolley(getInTrolleyTotal(items));
        setItemsCancelled(getCancelledTotal(items));
    }, [selectedRetailer, wmsItems]);

    // switch to validated tab if no items to pick
    useEffect(() => {
        if (itemsToPick === 0) {
            setFilterValue(1);
        }
    }, [itemsToPick]);

    // when retailer items change or filter value, we filter them based on the filterValue
    useEffect(() => {
        if (!retailerItems || retailerItems.length === 0) {
            return;
        }

        let items = [];

        setCancelledItems(retailerItems.filter(item => item.status === WmsItemsStatus.CANCELED));

        if (filterValue === 0) {
            items = retailerItems.filter(item => item.status === WmsItemsStatus.PICKING);
        } else if (filterValue === 1) {
            items = retailerItems.filter(item => item.status === WmsItemsStatus.IN_TROLLEY);
        } else {
            return;
        }

        setFilteredItems(items)
    }, [filterValue, retailerItems]);

    // when filter change in previous view
    useEffect(() => {
        setFilterValue(selectedFilter);
    }, [selectedFilter]);

    return (
        <BottomDrawer
            title={<RetailerNameDrawerTitle wmsItems={retailerItems}/>}
            open={open}
            onClose={() => {
                handleClose();
                setFilterValue(selectedFilter);
            }}
            disablePortal={true}
            openMaxHeight
            noHeightLimit
        >
            <div style={{background: '#fff'}}>
                <div style={{padding: 16, borderBottom: `1px solid ${greyishBlue50}`}}>
                    <ToggleNavigation
                        value={filterValue}
                        onChange={(index) => setFilterValue(index)}
                    >
                        <Toggle count={itemsToPick}>
                            <FormattedMessage id='wms.myPickingMissions.navigation.retrieve' />
                        </Toggle>
                        <Toggle count={itemsInTrolley + itemsCancelled}>
                            <FormattedMessage id='wms.myPickingMissions.navigation.validated' />
                        </Toggle>
                    </ToggleNavigation>
                </div>
                <RetailerItemsList
                    retailerItems={retailerItems}
                    wmsItems={filteredItems}
                    cancelledItems={cancelledItems}
                    handleCloseRetailerDrawer={handleClose}
                    itemsInTrolley={itemsInTrolley}
                    itemsCancelled={itemsCancelled}
                    filterValue={filterValue}
                    onItemClick={(wmsItem) => setSelectedWmsItem(wmsItem)}
                />
                <WmsItemInformationDrawer
                    selectedWmsItem={selectedWmsItem}
                    handleClose={() => setSelectedWmsItem(null)}
                >
                    {selectedWmsItem && selectedWmsItem.status === WmsItemsStatus.PICKING ? (
                        <PickingActions
                            selectedWmsItem={selectedWmsItem}
                            handleCloseDrawer={() => setSelectedWmsItem(null)}
                        />
                    ) : selectedWmsItem && selectedWmsItem.status === WmsItemsStatus.IN_TROLLEY && selectedWmsItem.wmsContainer !== null ? (
                        <RemoveFromTrolleyAction
                            selectedWmsItem={selectedWmsItem}
                            onRemoveFromTrolley={() => refreshMission()}
                            handleCloseDrawer={() => setSelectedWmsItem(null)}
                        />
                    ) : null}
                </WmsItemInformationDrawer>
            </div>
        </BottomDrawer>
    );
}

export default RetailerItemsDrawer;
